import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { FlexBox } from '../box';
import { getIcon } from '../icon';
import { styled } from '../styled';
import { Typography } from '../typography';
import { withVersion } from '../utils';
const ControlItemButton = styled('button')(({ theme, $active }) => ({
    padding: `${theme.spacing(0.25)} ${theme.spacing(0.75)}`,
    cursor: 'pointer',
    borderWidth: 0,
    boxSizing: 'border-box',
    display: 'flex',
    flex: '0 1 auto',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    borderRadius: theme.spacing(0.5),
    ':hover': {
        backgroundColor: theme.palette.common.black_states.hover
    },
    ':focus-visible': {
        backgroundColor: theme.palette.common.black_states.focus,
        outline: 'none'
    },
    ...($active
        ? {
            backgroundColor: theme.palette.components?.['segment-control']?.['background-active'] ?? theme.palette.common.white_states.main,
            color: theme.palette.components?.['segment-control']?.['label-active'],
            boxShadow: '0px 1px 1px 0px rgba(0, 0, 0, 0.07)',
            ':hover': {
                backgroundColor: 'none'
            }
        }
        : {})
}));
const SegmentControlItem = ({ icon, label, ariaLabel, iconProps, typographyProps, active, onClick, ...props }) => {
    const Icon = icon ? getIcon(icon) : () => null;
    const iconSizeSx = {
        fontSize: (theme) => theme.spacing(2)
    };
    const _iconProps = iconProps?.fontSize
        ? iconProps
        : {
            ...iconProps,
            sx: iconProps?.sx
                ? [
                    iconSizeSx,
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    ...(Array.isArray(iconProps.sx)
                        ? iconProps.sx
                        : [iconProps.sx])
                ]
                : iconSizeSx
        };
    return (_jsxs(ControlItemButton, { type: "button", "aria-label": ariaLabel ?? label, "aria-pressed": active, "$active": active, onClick: onClick, ...props, children: [_jsx(Icon, { ..._iconProps, color: active
                    ? 'inherit'
                    : 'components.segment-control.icon-default' }), _jsx(Typography, { fontWeight: 500, ...typographyProps, color: active ? 'inherit' : 'text.secondary', children: label })] }));
};
const SegmentControlImpl = ({ items = [], selectedSegmentIndex, onSegmentChange, ...props }) => {
    const [internalSelectedIndex, setInternalSelectedIndex] = React.useState(selectedSegmentIndex ?? 0);
    const isControlled = selectedSegmentIndex !== undefined;
    const activeSegmentIndex = isControlled
        ? selectedSegmentIndex
        : internalSelectedIndex;
    React.useEffect(() => {
        if (isControlled && selectedSegmentIndex !== internalSelectedIndex) {
            setInternalSelectedIndex(selectedSegmentIndex);
        }
    }, [selectedSegmentIndex, isControlled, internalSelectedIndex]);
    return (_jsx(FlexBox, { ...props, bgcolor: "components.segment-control.background", stretch: false, width: "fit-content", gap: 0.25, padding: 0.25, borderRadius: 1, children: items.map((item, index) => (_jsx(SegmentControlItem, { ...item, active: index === activeSegmentIndex, onClick: () => {
                if (!isControlled) {
                    setInternalSelectedIndex(index);
                }
                onSegmentChange?.(index);
            } }, item['data-role'] ?? index))) }));
};
export const SegmentControl = withVersion(SegmentControlImpl, '1.0.0');
